import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Edit, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState } from "react";
import { sanitizeHtml } from '@/lib/sanitizer';

const NoticeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const { toast } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const { data: notice, isLoading, error } = useQuery({
    queryKey: ["notice", id],
    queryFn: async () => {
      if (!id) throw new Error("Notice ID is required");
      
      const { data, error } = await supabase
        .from("notices")
        .select("*")
        .eq("id", id)
        .eq("is_published", true)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Notice not found");

      return data;
    },
    enabled: !!id,
  });

  const handleEdit = () => {
    if (!notice) return;
    setTitle(notice.title);
    setContent(notice.content);
    setPriority(notice.priority);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isAdmin || !notice) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('notices')
        .update({
          title,
          content,
          priority,
        })
        .eq('id', notice.id);

      if (error) throw error;

      toast({
        title: "성공",
        description: "공지사항이 수정되었습니다."
      });

      setEditDialogOpen(false);
      // Refresh the notice data
      window.location.reload();
    } catch (error) {
      console.error('Error updating notice:', error);
      toast({
        title: "오류",
        description: "공지사항 수정에 실패했습니다.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !isAdmin || !notice) return;

    try {
      const { error } = await supabase
        .from('notices')
        .delete()
        .eq('id', notice.id);

      if (error) throw error;

      toast({
        title: "성공",
        description: "공지사항이 삭제되었습니다."
      });

      navigate('/notice');
    } catch (error) {
      console.error('Error deleting notice:', error);
      toast({
        title: "오류",
        description: "공지사항 삭제에 실패했습니다.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="py-20 bg-gradient-section">
          <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Skeleton className="h-10 w-32 mb-4" />
            </div>
            <Card className="shadow-clean border-0">
              <CardHeader>
                <Skeleton className="h-8 w-3/4 mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="py-20 bg-gradient-section">
          <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground mb-4">
                공지사항을 찾을 수 없습니다
              </h1>
              <p className="text-muted-foreground mb-8">
                요청하신 공지사항이 존재하지 않거나 삭제되었을 수 있습니다.
              </p>
              <Link to="/notice">
                <Button className="bg-jax-blue hover:bg-jax-blue/90 text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  공지사항 목록으로 돌아가기
                </Button>
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead 
        title={notice ? `${notice.title} - Central TG 공지사항` : "공지사항 - Central TG"}
        description={notice ? notice.content.replace(/<[^>]*>/g, '').substring(0, 160) : "Central TG의 공지사항을 확인하세요."}
        keywords="Central TG 공지, JAX 모델 소식, transgenic 마우스 업데이트"
        canonical={`https://centraltg.co.kr/notice/${id}`}
      />
      <Header />
      <section className="py-20 bg-gradient-section">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <Link to="/notice">
                <Button variant="ghost" className="hover:bg-white/80 text-sm sm:text-base">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="hidden xs:inline">공지사항 목록으로 돌아가기</span>
                  <span className="xs:hidden">목록</span>
                </Button>
              </Link>
              
              {user && isAdmin && (
                <div className="flex flex-col xs:flex-row gap-2 w-full sm:w-auto">
                  <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full xs:w-auto" onClick={handleEdit}>
                        <Edit className="w-4 h-4 mr-2" />
                        수정
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>공지사항 수정</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleEditSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="edit-title">제목</Label>
                          <Input
                            id="edit-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-priority">우선순위</Label>
                          <select
                            id="edit-priority"
                            value={priority}
                            onChange={(e) => setPriority(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-input rounded-md"
                          >
                            <option value={1}>일반</option>
                            <option value={2}>중요</option>
                            <option value={3}>긴급</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="edit-content">내용</Label>
                          <ScrollArea className="h-[400px] w-full border rounded-md [&>[data-radix-scroll-area-viewport]]:!block">
                            <div className="p-1">
                              <RichTextEditor
                                content={content}
                                onChange={setContent}
                                placeholder="공지사항 내용을 입력하세요"
                              />
                            </div>
                          </ScrollArea>
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setEditDialogOpen(false)}
                          >
                            취소
                          </Button>
                          <Button type="submit" disabled={submitting}>
                            {submitting ? "수정 중..." : "수정"}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" className="w-full sm:w-auto">
                        <Trash2 className="w-4 h-4 mr-2" />
                        삭제
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>공지사항 삭제</AlertDialogTitle>
                        <AlertDialogDescription>
                          이 공지사항을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>취소</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>
                          삭제
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>

          <Card className="shadow-clean border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3 mb-4">
                {notice.priority >= 3 ? (
                  <Badge variant="destructive" className="text-sm shadow-sm">
                    긴급
                  </Badge>
                ) : notice.priority === 2 ? (
                  <Badge variant="default" className="text-sm shadow-sm">
                    중요
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-sm shadow-sm">
                    일반
                  </Badge>
                )}
              </div>
              
              <h1 className="text-3xl font-bold text-foreground mb-4">
                {notice.title}
              </h1>
              
              <div className="flex items-center text-muted-foreground">
                <Calendar className="w-4 h-4 mr-2" />
                {new Date(notice.created_at).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </CardHeader>
            
            <CardContent>
              <div 
                className="prose prose-sm max-w-none text-foreground [&_table]:border-collapse [&_table]:border [&_table]:border-border [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:p-3 [&_th]:border [&_th]:border-border [&_th]:p-3 [&_th]:bg-muted/50 [&_th]:font-semibold [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary/80 [&_a]:font-medium [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:shadow-sm [&_img]:border [&_img]:my-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h1]:leading-tight [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:mt-5 [&_h2]:leading-tight [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:leading-tight [&_p]:mb-3 [&_p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.content) }}
              />
            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default NoticeDetail;